import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as equipmentApi from '../api';
import type { DocumentKind, EquipmentModelInput, ModelStatus, ReviewPhotos, SubmissionStatus } from '../types';

export const equipmentKeys = {
  all: ['equipment'] as const,
  list: (status: ModelStatus) => [...equipmentKeys.all, 'list', status] as const,
  detail: (id: string) => [...equipmentKeys.all, 'detail', id] as const,
  sensors: (id: string) => [...equipmentKeys.all, 'sensors', id] as const,
  submissions: (status: SubmissionStatus) => [...equipmentKeys.all, 'submissions', status] as const,
  submission: (id: string) => [...equipmentKeys.all, 'submission', id] as const,
  fileUrl: (kind: 'documents' | 'photos', id: string) => [...equipmentKeys.all, 'file-url', kind, id] as const,
};

/** Signed URLs live 10 minutes on the server; refetch before they expire. */
const FILE_URL_STALE_MS = 8 * 60 * 1000;

function errorDescription(error: unknown) {
  return error instanceof Error ? error.message : undefined;
}

function useEquipmentMutation<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  messages: { success?: string; error: string },
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipmentKeys.all });
      if (messages.success) toast.success(messages.success);
    },
    onError: (error) => {
      toast.error(messages.error, { description: errorDescription(error) });
    },
  });
}

export function useEquipmentModels(status: ModelStatus) {
  return useQuery({ queryKey: equipmentKeys.list(status), queryFn: () => equipmentApi.getEquipmentModels(status) });
}

export function useEquipmentModel(id: string | null) {
  return useQuery({
    queryKey: equipmentKeys.detail(id ?? ''),
    queryFn: () => equipmentApi.getEquipmentModel(id as string),
    enabled: !!id,
  });
}

export function useEquipmentModelSensors(id: string | null) {
  return useQuery({
    queryKey: equipmentKeys.sensors(id ?? ''),
    queryFn: () => equipmentApi.getEquipmentModelSensors(id as string),
    enabled: !!id,
  });
}

export function useEquipmentFileUrl(kind: 'documents' | 'photos', id: string | null) {
  return useQuery({
    queryKey: equipmentKeys.fileUrl(kind, id ?? ''),
    queryFn: () => equipmentApi.getEquipmentFileUrl(kind, id as string),
    enabled: !!id,
    staleTime: FILE_URL_STALE_MS,
  });
}

/** The review queue is reviewer-only (gateway: equipment.review); pass `enabled` from the permission. */
export function useSubmissions(status: SubmissionStatus, enabled = true) {
  return useQuery({ queryKey: equipmentKeys.submissions(status), queryFn: () => equipmentApi.getSubmissions(status), enabled });
}

export const useCreateEquipmentModel = () =>
  useEquipmentMutation((input: EquipmentModelInput) => equipmentApi.createEquipmentModel(input), {
    success: 'Equipment model created', error: 'Failed to create equipment model',
  });

export const useUpdateEquipmentModel = () =>
  useEquipmentMutation(({ id, data }: { id: string; data: Partial<EquipmentModelInput> }) => equipmentApi.updateEquipmentModel(id, data), {
    error: 'Failed to save equipment model',
  });

export const useArchiveEquipmentModel = () =>
  useEquipmentMutation((id: string) => equipmentApi.archiveEquipmentModel(id), { success: 'Model archived', error: 'Failed to archive model' });

export const useRestoreEquipmentModel = () =>
  useEquipmentMutation((id: string) => equipmentApi.restoreEquipmentModel(id), { success: 'Model restored', error: 'Failed to restore model' });

export const useAddEquipmentAlias = () =>
  useEquipmentMutation(({ id, brand, model }: { id: string; brand: string; model: string }) => equipmentApi.addEquipmentAlias(id, { brand, model }), {
    success: 'Alias added', error: 'Failed to add alias',
  });

export const useDeleteEquipmentAlias = () =>
  useEquipmentMutation(({ id, aliasId }: { id: string; aliasId: string }) => equipmentApi.deleteEquipmentAlias(id, aliasId), {
    error: 'Failed to remove alias',
  });

export const useUploadEquipmentDocument = () =>
  useEquipmentMutation(
    ({ id, file, kind, title, pageHint }: { id: string; file: File; kind: DocumentKind; title: string; pageHint?: string }) =>
      equipmentApi.uploadEquipmentDocument(id, file, { kind, title, pageHint }),
    { success: 'Document uploaded', error: 'Failed to upload document' },
  );

export const useLinkEquipmentDocument = () =>
  useEquipmentMutation(
    ({ id, url, kind, title }: { id: string; url: string; kind: DocumentKind; title: string }) => equipmentApi.linkEquipmentDocument(id, { url, kind, title }),
    { success: 'Link added', error: 'Failed to add link' },
  );

export const useDeleteEquipmentDocument = () =>
  useEquipmentMutation(({ id, documentId }: { id: string; documentId: string }) => equipmentApi.deleteEquipmentDocument(id, documentId), {
    error: 'Failed to remove document',
  });

export const useUploadEquipmentPhoto = () =>
  useEquipmentMutation(
    ({ id, file, kind, caption }: { id: string; file: File; kind: 'product' | 'install'; caption?: string }) =>
      equipmentApi.uploadEquipmentPhoto(id, file, { kind, caption }),
    { success: 'Photo uploaded', error: 'Failed to upload photo' },
  );

export const useDeleteEquipmentPhoto = () =>
  useEquipmentMutation(({ id, photoId }: { id: string; photoId: string }) => equipmentApi.deleteEquipmentPhoto(id, photoId), {
    error: 'Failed to remove photo',
  });


export function useSubmission(id: string | null) {
  return useQuery({
    queryKey: equipmentKeys.submission(id ?? ''),
    queryFn: () => equipmentApi.getSubmission(id as string),
    enabled: !!id,
  });
}

export const useApproveSubmission = () =>
  useEquipmentMutation(
    ({ id, ...input }: { id: string; model: EquipmentModelInput } & ReviewPhotos) => equipmentApi.approveSubmission(id, input),
    { success: 'Submission approved as a new model', error: 'Failed to approve submission' },
  );

export const useMergeSubmission = () =>
  useEquipmentMutation(
    ({ id, ...input }: { id: string; modelId: string } & ReviewPhotos) => equipmentApi.mergeSubmission(id, input),
    { success: 'Submission merged into the model', error: 'Failed to merge submission' },
  );

export const useRejectSubmission = () =>
  useEquipmentMutation(({ id, reason }: { id: string; reason: string }) => equipmentApi.rejectSubmission(id, reason), {
    success: 'Submission rejected', error: 'Failed to reject submission',
  });
